// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';
import invariant from 'invariant';
import type { CurrentUser, UserOrgMini, UserGroupMini } from 'api/currentUser';
import { partialUpdateOrg as apiUpdateOrg } from 'api/orgAdmin';
import type { ImageData } from 'api/shared';
import type { Org as ApiOrg } from 'api/orgs';
import type { VolopImportedType } from 'api/volops';
import { getIn } from 'utils/functional';
import { pathWithParams } from 'utils/url';
import {
  VOLOP_IMPORTED_TYPE_MAP,
  LISTING_TYPE_URL_SEGMENT_MAP,
  SEARCH_HIT_IMAGE_WIDTH,
  SEARCH_HIT_IMAGE_WIDTH_SMALL
} from 'utils/constants';
import Box from 'components/Box';
import ListingStatusLabel, {
  LocationLabel,
  Tag
} from 'components/ListingStatusLabel';
import { trackClicked } from 'utils/analytics';
import {
  ButtonBase,
  PrimaryButtonRouterLink,
  PrimaryButton
} from 'components/Button';
import Divider from 'components/Divider';
import FundingMeter from 'components/FundingMeter';
import Icon from 'components/Icon';
import VolopIcon from 'components/VolopIcon';
import { LogoImage, Placeholder as LogoPlaceholder } from 'components/Image';
import { Link as ReactRouterLink } from 'components/RouterLink';
import { withRouter } from 'react-router';
import type { RouterHistory } from 'react-router';
import { getCityStateString } from 'utils/address';
import { distanceInWordsToNow, shorterDate, shorterTime } from 'utils/date';
import scrollElementIntoView from 'utils/scrollIntoView';
import { retinaBackgroundImage } from 'utils/style';
import { imageUrlDeprecated as getImageUrl } from 'utils/filestack';
import ListingTypeLabel from 'components/ListingTypeLabel';
import Text, { H4, H5, BodySmall, BodyMicro } from 'components/Text';
import Hide from 'components/Hide';
import { SmallTile } from 'components/Tile';
import { HoverEffect } from 'components/SearchTile';
import Form, { LogoPickerField } from 'components/Form';
import { NAME_MAP } from 'layouts/ListingLayout';
import Tooltip from 'components/Tooltip';
import HideListingContainer from 'containers/HideListingContainer';
import HideGroupVolopContainer from 'containers/HideGroupVolopContainer';
import { getCountryName } from 'utils/internationalization';
import type { SearchHit as SearchHitType } from 'api/search';
import { partialUpdateOrg as partialUpdateOrgRaw } from '../redux/main/actions/orgs';
import { breakpoints, colors, transition, fontWeights } from '../theme';

const { useRef, useEffect } = React;

const LogoBox = styled(Box)`
  /* styles */
`;
const LabelBox = styled(Box)`
  /* styles */
`;
const Heading = styled(H4)`
  /* styles */
`;
const Subheading = styled(H5)`
  /* styles */
`;
const Info = styled(BodyMicro)`
  /* styles */
`;
const DateInfo = styled(BodyMicro)`
  /* styles */
`;
const Stats = styled(props => <Text fontSize={12} {...props} />)`
  /* styled */
`;

const InfoBox = styled(Box)`
  ${Heading} {
    margin: 0;
    & + ${Subheading} {
      margin-top: 7px;
    }
    & + ${Info} {
      margin-top: 9px;
    }
    & + ${DateInfo} {
      margin-top: 9px;
    }
  }
  ${Info} {
    margin-top: 9px;
  }
  ${DateInfo} {
    margin-top: 9px;
  }
  ${Stats} {
    margin-top: 9px;
  }
`;

const Actions = styled(Box)`
  @media (min-width: ${breakpoints[1]}) {
    border-left: 1px solid ${colors.aluminum};
  }
`;

const filestackTransform = (width: number, height: number) =>
  `resize=width:${width},height:${height},fit:crop,align:center/quality=value:70`;

const SmallImage = styled(Box)`
  ${props =>
    props.$url &&
    `
    background-image: url('${props.$url.replace(/^http:/, 'https:')}');
    `}
  ${props =>
    props.$handle &&
    retinaBackgroundImage(
      getImageUrl({
        handle: props.$handle,
        transform: filestackTransform(
          SEARCH_HIT_IMAGE_WIDTH,
          SEARCH_HIT_IMAGE_WIDTH
        )
      }),
      {
        filename2x: getImageUrl({
          handle: props.$handle,
          transform: filestackTransform(
            SEARCH_HIT_IMAGE_WIDTH * 2,
            SEARCH_HIT_IMAGE_WIDTH * 2
          )
        }),
        include3x: false
      }
    )}
  img {
    display: block;
  }
`;

const getImportedUrl = (url, source) =>
  source && source === 'DO_SOMETHING'
    ? pathWithParams(url, {
        utm_medium: 'referral',
        utm_source: 'partner',
        utm_campaign: 'idealist_vcredit',
        utm_content: 'vcredit'
      })
    : pathWithParams(url, {
        utm_medium: 'referral',
        utm_source: 'idealist',
        utm_campaign: 'action_opportunities'
      });

const ActionButtons = ({
  hit,
  org,
  group,
  history,
  type
}: {
  hit: SearchHitType,
  org: ?UserOrgMini,
  group: ?UserGroupMini,
  history: RouterHistory,
  type: string
}) => {
  invariant(hit.type !== 'IMPORTED', 'hit.type is "IMPORTED"');
  const viewUrl =
    type === 'ORG' && org
      ? `/admin/${org.id}`
      : type === 'GROUP' && group
      ? `/group-admin/${group.id}`
      : type === 'VOLOP' && group
      ? `/group-admin/${group.id}/${LISTING_TYPE_URL_SEGMENT_MAP[hit.type]}/${
          hit.objectID
        }`
      : org
      ? `/admin/${org.id}/${LISTING_TYPE_URL_SEGMENT_MAP[hit.type]}/${
          hit.objectID
        }`
      : '';
  return (
    <>
      <PrimaryButtonRouterLink
        to={`${viewUrl}/edit`}
        data-qa-id="edit-button"
        size="small"
      >
        {getText('Edit')}
      </PrimaryButtonRouterLink>
      {type !== 'ORG' && type !== 'GROUP' && (
        <>
          {org && (
            <>
              <HideListingContainer
                listingId={hit.objectID}
                listingType={hit.type}
                orgId={org.id}
                render={({ hideListing, submitting }) => (
                  <PrimaryButton
                    onClick={e => {
                      e.stopPropagation();
                      hideListing().then(() => {
                        history.push(viewUrl, {
                          flashMessage: { type: 'LISTING_HIDDEN' }
                        });
                      });
                    }}
                    disabled={submitting}
                    data-qa-id="hide-button"
                    size="small"
                  >
                    {/* TRANSLATORS: hide listing, button text */ getText(
                      'Hide'
                    )}
                  </PrimaryButton>
                )}
              />
              <PrimaryButtonRouterLink
                to={`${viewUrl}/repost`}
                data-qa-id="repost-button"
                size="small"
                disabled={hit.type === 'VOLOP' && org && !org.canPostVolops}
              >
                {getText('Repost')}
              </PrimaryButtonRouterLink>
            </>
          )}
          {group && (
            <>
              <HideGroupVolopContainer
                volopId={hit.objectID}
                groupId={group.id}
                render={({ hideVolop, submitting }) => (
                  <PrimaryButton
                    onClick={e => {
                      e.stopPropagation();
                      hideVolop().then(() => {
                        history.push(viewUrl, {
                          flashMessage: { type: 'LISTING_HIDDEN' }
                        });
                      });
                    }}
                    disabled={submitting}
                    data-qa-id="hide-button"
                    size="small"
                  >
                    {/* TRANSLATORS: hide listing, button text */ getText(
                      'Hide'
                    )}
                  </PrimaryButton>
                )}
              />
              <PrimaryButtonRouterLink
                to={`${viewUrl}/repost`}
                data-qa-id="repost-button"
                size="small"
              >
                {getText('Repost')}
              </PrimaryButtonRouterLink>
            </>
          )}
        </>
      )}
    </>
  );
};

export const getCtaByType = ({
  type,
  actionType,
  source,
  baseUrl,
  hasPublishedListings,
  userHasApplied
}: {
  type: string,
  actionType?: string,
  source?: VolopImportedType,
  baseUrl: string,
  hasPublishedListings?: boolean,
  userHasApplied: boolean
}) => {
  if (['ORG', 'GROUP'].includes(type)) {
    if (hasPublishedListings) {
      return {
        text: getText('See Listings'),
        url: `${baseUrl}#section-opportunities`,
        analyticsText: 'See Listings'
      };
    }
    return {
      text: getText('View'),
      url: baseUrl,
      analyticsText: 'View'
    };
  }
  if (type === 'IDEALIST_DAY_EVENT') {
    return {
      text: getText('Join Us'),
      url: `${baseUrl}#section-rsvp`,
      analyticsText: 'Join Us'
    };
  }
  if (type === 'IMPORTED') {
    const importedUrl = getImportedUrl(baseUrl, source);

    return {
      text: getText('Visit'),
      url: importedUrl,
      analyticsText: 'Visit'
    };
  }
  if (type === 'VOLOP' && actionType === 'EVENT') {
    return userHasApplied
      ? {
          text: <CtaCompletedText text={getText('Joined')} />,
          url: baseUrl,
          analyticsText: 'Applied'
        }
      : {
          text: getText('Join'),
          url: `${baseUrl}#section-apply`,
          analyticsText: 'Join'
        };
  }
  return userHasApplied
    ? {
        text: <CtaCompletedText text={getText('Applied')} />,
        url: baseUrl,
        analyticsText: 'Applied'
      }
    : {
        text: getText('Apply'),
        url: `${baseUrl}#section-apply`,
        analyticsText: 'Apply'
      };
};

export const CtaCompletedText = ({ text }: { text: string }) => (
  <Box display="flex" justifyContent="center" alignItems="center">
    <Box
      mr={USER_LOCALE !== 'en' ? '8px' : undefined}
      color={colors.turtleGreen}
    >
      <Icon
        stroke="currentColor"
        name="checkmark-path"
        width={12}
        height={12}
        display="block"
      />
    </Box>
    <Box color={colors.battleshipGrey} flex="0">
      {text}
    </Box>
  </Box>
);

export const StyledButton = styled(
  ({ saved, ...props }: { saved?: boolean }) => (
    <ButtonBase {...props} size="small" />
  )
)`
  z-index: 2;
  box-sizing: border-box;
  display: flex;
  height: 40px;
  padding-left: 0;
  padding-right: 0;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  text-decoration: none;
  text-align: center;
  transition: all ${transition};
  svg * {
    transition-duration: ${transition};
    transition-property: stroke, fill;
  }
  svg {
    margin-right: 4px;
  }
  @media (min-width: ${breakpoints[1]}) {
    width: 100%;
  }
`;

const BaseCtaButton = ({
  external,
  url,
  ...props
}: {
  external: boolean,
  url: string
}) =>
  external ? (
    <StyledButton component="a" href={url} {...props} target="_blank" />
  ) : (
    <StyledButton component={ReactRouterLink} to={url} {...props} />
  );

const CtaButton = styled(
  ({
    hit,
    isImported,
    applications,
    ...props
  }: {
    hit: SearchHitType,
    isImported: boolean,
    applications?: Array<string>
  }) => {
    const cta = getCtaByType({
      type: hit.type,
      actionType: hit.actionType,
      source: hit.source,
      baseUrl: typeof hit.url === 'string' ? hit.url : hit.url[USER_LOCALE],
      hasPublishedListings: hit.hasPublishedListings,
      userHasApplied: !!(applications && applications.includes(hit.objectID))
    });

    return (
      <BaseCtaButton
        {...props}
        external={isImported}
        url={cta.url}
        onClick={() => {
          trackClicked('Search results CTA button', { type: hit.type });
        }}
      >
        <Box display="flex" alignItems="center">
          {cta.text}
          {isImported && (
            <Box ml="4px">
              <Icon
                width={12}
                height={12}
                stroke="currentColor"
                strokeWidth="8px"
                display="block"
                name="external-link"
              />
            </Box>
          )}
        </Box>
      </BaseCtaButton>
    );
  }
)`
  &:hover {
    color: white;
    background: ${colors.waterBlue};
    * {
      color: white;
    }
  }
`;

export const SecondaryButton = styled(StyledButton)`
  color: ${colors.battleshipGrey};
  svg {
    * {
      fill: none;
      stroke: currentColor;
      stroke-width: 2px;
    }
  }
  &:hover {
    background: ${colors.platinum};
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  }
  ${props =>
    props.saved &&
    `
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  `}
`;

const RouterLink = styled(ReactRouterLink)`
  &,
  &:active,
  &:visited {
    color: inherit;
    text-decoration: none;
    position: relative;
    z-index: 2;
  }
  &:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

const Link = styled.a`
  &,
  &:active,
  &:visited {
    color: inherit;
    text-decoration: none;
    position: relative;
    z-index: 2;
  }
  &:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

export const confirmModal = {
  type: 'danger',
  title: getText('Remove Image'),
  buttonText: getText('Remove'),
  body: getText('Are you sure you want to remove this image?')
};

export const SearchTileContainer = styled(Box)`
  * {
    pointer-events: none;
  }
  a,
  button {
    pointer-events: auto;
  }
`;

const StyledRouterLink = styled(RouterLink)`
  transition: all ${transition};
  &:hover {
    color: ${colors.waterBlue};
    text-decoration: none;
  }
`;

const listingLinkStyles = `
  cursor: pointer;
  transition: all ${transition};
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
    text-decoration: none;
    & + ${HoverEffect} {
      &::before {
        transform: translateY(0);
      }
    }
  }
`;

const ListingLink = styled(Link)`
  ${listingLinkStyles}
`;

export const ListingRouterLink = styled(RouterLink)`
  ${listingLinkStyles}
`;

export const SearchTileLink = styled(
  ({
    url,
    listingType,
    trackClick,
    source,
    ...props
  }: {
    url: string | Object,
    listingType: string,
    trackClick?: Function,
    source?: VolopImportedType
  }) =>
    typeof url === 'object' ? (
      <ReactRouterLink
        {...props}
        to={{
          pathname: url[USER_LOCALE],
          state: {
            showSearchContext: true,
            originatingPathname: window.location.pathname
          }
        }}
        onClick={() => {
          if (trackClick) trackClick();
        }}
        tabIndex="-1"
      />
    ) : listingType === 'IMPORTED' ? (
      <a
        {...props}
        href={getImportedUrl(url, source)}
        onClick={() => {
          if (trackClick) trackClick();
        }}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex="-1"
      />
    ) : (
      <a
        {...props}
        href={url}
        onClick={() => {
          if (trackClick) trackClick();
        }}
        tabIndex="-1"
      />
    )
)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: block;
  &:focus {
    outline: 0;
  }
  & + * {
    ${HoverEffect} {
      z-index: 1;
      &::before {
        transform: translateY(-100%);
      }
    }
  }
  &:hover,
  &:focus {
    & + * {
      ${HoverEffect} {
        &::before {
          transform: translateY(0);
        }
      }
      ${ListingLink}, ${ListingRouterLink} {
        color: ${colors.waterBlue};
      }
    }
  }
`;

const LogoContainer = styled(Box)`
  ${props =>
    props.$showLogoUpload &&
    `
    z-index: 2;
    pointer-events: auto;
    * {
      pointer-events: auto;
    }
  `}
  img {
    max-height: 90px;
  }
`;

type OwnProps = {
  hit: SearchHitType,
  scrollIntoView?: boolean,
  saveListing: () => void,
  unsaveListing: () => void,
  saved: boolean,
  canSave: boolean,
  'data-qa-id'?: string,
  history: RouterHistory,
  trackClick?: () => void,
  showLabel?: boolean,
  hidePublished?: boolean,
  hideCta?: boolean,
  disabled?: boolean,
  application: {
    status: 'ACTIVE' | 'INACTIVE',
    created: string
  },
  applications?: Array<string>,
  currentUser: CurrentUser
};

type Props = {
  ...$Exact<OwnProps>,
  partialUpdateOrg: Object => void,
  ownedOrgsById: { [string]: ApiOrg }
};

const SearchHit = React.memo(
  ({
    hit,
    saveListing,
    unsaveListing,
    saved,
    canSave,
    history,
    trackClick,
    scrollIntoView,
    currentUser,
    partialUpdateOrg,
    ownedOrgsById,
    showLabel,
    hidePublished,
    hideCta,
    disabled,
    application,
    applications,
    'data-qa-id': qaId
  }: Props) => {
    const hitRef = useRef(null);

    const {
      name,
      type,
      published,
      logo: searchResultLogo,
      imageHandle,
      imageUrl,
      orgUrl,
      orgType,
      groupName,
      groupUrl,
      isFullTime,
      listingLocationType,
      remoteZone,
      city,
      state,
      stateStr,
      country,
      url,
      startDate,
      startTime,
      startsLocal,
      endDate,
      endTime,
      endsLocal,
      localizedStarts,
      localizedEnds,
      timezone,
      objectID,
      amountRaised,
      amountRequested,
      signatureCount,
      goal,
      source,
      actionType,
      isPostedAnonymously
    } = hit;

    const amountCurrent = amountRaised || signatureCount;
    const amountTotal = amountRequested || goal;

    useEffect(
      () => {
        if (scrollIntoView && hitRef && hitRef.current) {
          scrollElementIntoView(hitRef.current);
        }
      },
      [scrollIntoView]
    );
    const updateOrg = values => {
      apiUpdateOrg(values).then(partialUpdateOrg);
    };

    const location =
      remoteZone !== undefined &&
      remoteZone === 'WORLD' &&
      listingLocationType !== 'REMOTE_TEMPORARY_COVID'
        ? getText('Anywhere in the world')
        : remoteZone !== undefined &&
          remoteZone === 'COUNTRY' &&
          listingLocationType !== 'REMOTE_TEMPORARY_COVID'
        ? getCountryName(country)
        : getCityStateString({
            city,
            stateCode: state || stateStr,
            country
          });
    const isImported = type === 'IMPORTED';

    const listingInfo = [];
    if (location) {
      listingInfo.push(location);
    }
    if (isFullTime !== undefined) {
      listingInfo.push(
        isFullTime ? getText('Full Time') : getText('Part Time')
      );
    }

    const listingStats = [];
    if (published && !hidePublished && source !== 'DO_SOMETHING') {
      listingStats.push(
        sprintf(getText('Posted %(postedAt)s'), {
          postedAt: distanceInWordsToNow(published * 1000)
        })
      );
    }
    if (published && !hidePublished && source === 'DO_SOMETHING') {
      listingStats.push(/* TRANSLATORS Ongoing */ getText('Ongoing'));
    }
    if (isImported && hit.source) {
      listingStats.push(VOLOP_IMPORTED_TYPE_MAP[hit.source]);
    }
    if (actionType && !isImported) {
      listingStats.push(
        <Box
          display="inline"
          color={colors.waterBlue}
          fontWeight={fontWeights.semiBold}
        >
          Idealist
        </Box>
      );
    }

    const showCta =
      !hideCta &&
      [
        'IDEALIST_DAY_EVENT',
        'GROUP',
        'ORG',
        'JOB',
        'VOLOP',
        'INTERNSHIP',
        'IMPORTED'
      ].includes(type);

    const orgId = type === 'ORG' ? objectID : hit.orgID;
    const orgName = type === 'ORG' ? name : hit.orgName;
    const groupId = type === 'GROUP' ? objectID : hit.groupID;
    const ownerOrg =
      currentUser &&
      currentUser.orgs &&
      currentUser.orgs.find(o => o.id === orgId);
    const ownerGroup =
      currentUser &&
      currentUser.groups &&
      currentUser.groups.find(g => g.id === groupId);
    const logo =
      getIn(ownedOrgsById, [orgId || '', 'logo']) || searchResultLogo;
    const showLogoUpload = orgType !== 'CORPORATION' && ownerOrg && !logo;

    const startDateTime =
      startDate && startTime ? startsLocal : localizedStarts || null;
    const endDateTime = endDate && endTime ? endsLocal : localizedEnds || null;

    return (
      <div ref={hitRef} data-qa-id={qaId || 'search-result'}>
        {(ownerOrg || ownerGroup) && (
          <Box
            display={['block', 'flex']}
            alignItems="center"
            justifyContent="space-between"
            p={['12px 20px', '16px 4px 16px 20px', '16px 12px 16px 30px']}
            zIndex="3"
            color="white"
            bg={colors.waterBlue}
            borderRadius="4px 4px 0 0"
            mb={-10}
          >
            <BodySmall>
              {type === 'ORG'
                ? getText('You are an administrator of this organization')
                : type === 'GROUP'
                ? getText('You are an administrator of this group')
                : type === 'VOLOP' && ownerGroup
                ? getText('Your group posted this volunteer opportunity')
                : sprintf(
                    getText(
                      'Your organization posted this %(listingTypeName)s'
                    ),
                    {
                      listingTypeName: NAME_MAP[type]
                    }
                  )}
            </BodySmall>
            <Box mt={['8px', 0]} ml={[-16, 0]} flex="0 0 auto">
              <ActionButtons
                type={type}
                org={ownerOrg}
                group={ownerGroup}
                hit={hit}
                history={history}
              />
            </Box>
          </Box>
        )}
        <SearchTileContainer
          position="relative"
          my={10}
          color={disabled && colors.battleshipGrey}
          cursor={disabled ? 'auto' : 'pointer'}
        >
          {!disabled && (
            <SearchTileLink
              trackClick={trackClick}
              url={url}
              aria-hidden="true"
              listingType={type}
              source={source}
            />
          )}
          <SmallTile
            overflow="visible !important"
            pr={[undefined, null, '16px !important']}
            my="0 !important"
            borderRadius={ownerOrg && '0 0 4px 4px !important'}
            disabled={disabled}
          >
            <Box display={['block', null, 'flex']} pr={[undefined, 0, 0, 0]}>
              <Box
                display="flex"
                width="100%"
                flex="1 1 auto"
                pr={32}
                flexDirection={['column', 'row', null, null]}
              >
                <LogoBox
                  flex="0 0 auto"
                  pr={[0, 30]}
                  pb={[24, 0]}
                  display={[isImported || !logo ? 'none' : 'flex', 'flex']}
                >
                  {isImported && !imageUrl && source ? (
                    <VolopIcon
                      name={source.toLowerCase().replace(/_/g, '-')}
                      width={[
                        SEARCH_HIT_IMAGE_WIDTH_SMALL,
                        null,
                        null,
                        null,
                        SEARCH_HIT_IMAGE_WIDTH
                      ]}
                      maxHeight={90}
                      m="0 auto"
                    />
                  ) : imageUrl || imageHandle ? (
                    <SmallImage
                      $url={imageUrl}
                      $handle={imageHandle}
                      width={[
                        SEARCH_HIT_IMAGE_WIDTH_SMALL,
                        null,
                        null,
                        null,
                        SEARCH_HIT_IMAGE_WIDTH
                      ]}
                      height={[
                        SEARCH_HIT_IMAGE_WIDTH_SMALL,
                        null,
                        null,
                        null,
                        SEARCH_HIT_IMAGE_WIDTH
                      ]}
                      backgroundPosition="center"
                      backgroundSize="cover"
                      borderRadius="4px"
                    />
                  ) : (
                    <LogoContainer
                      position="relative"
                      pointerEvents={showLogoUpload ? 'auto' : undefined}
                      $showLogoUpload={showLogoUpload}
                    >
                      {showLogoUpload ? (
                        <Form
                          onSubmit={() => undefined}
                          render={({ getValue, updateValues }) => {
                            const logoId = `logo-${objectID}`;
                            const image = ((getValue(logoId): any): ImageData);
                            return image ? (
                              <Box data-qa-id="logo-uploaded-image">
                                <LogoImage
                                  alignItems="flex-start"
                                  width={[
                                    SEARCH_HIT_IMAGE_WIDTH_SMALL,
                                    null,
                                    null,
                                    null,
                                    SEARCH_HIT_IMAGE_WIDTH
                                  ]}
                                  fsWidth={SEARCH_HIT_IMAGE_WIDTH}
                                  handle={image ? image.handle : undefined}
                                  mimetype={image ? image.mimetype : undefined}
                                  alt={name}
                                  initial={orgName || groupName || name}
                                />
                              </Box>
                            ) : (
                              <Box my={-12}>
                                <LogoPickerField
                                  name={logoId}
                                  altText={orgName}
                                  centerButton
                                  onChange={(newLogo: ImageData) => {
                                    updateValues({ [logoId]: newLogo });
                                    updateOrg({ id: orgId, logo: newLogo });
                                  }}
                                  message={getText('Upload Logo')}
                                  confirmModal={confirmModal}
                                  size={92}
                                  buttonOnly
                                  previewHeight={[
                                    SEARCH_HIT_IMAGE_WIDTH_SMALL,
                                    null,
                                    null,
                                    null,
                                    SEARCH_HIT_IMAGE_WIDTH
                                  ]}
                                  previewWidth={[
                                    SEARCH_HIT_IMAGE_WIDTH_SMALL,
                                    null,
                                    null,
                                    null,
                                    SEARCH_HIT_IMAGE_WIDTH
                                  ]}
                                  hideRemove
                                  noMargin
                                  data-qa-id="logo"
                                />
                              </Box>
                            );
                          }}
                        />
                      ) : orgType === 'CORPORATION' ? (
                        <LogoPlaceholder
                          width={[
                            SEARCH_HIT_IMAGE_WIDTH_SMALL,
                            null,
                            null,
                            null,
                            SEARCH_HIT_IMAGE_WIDTH
                          ]}
                          initial={orgName || groupName || name}
                          bg={colors.silver}
                          color="white"
                        />
                      ) : (
                        <LogoImage
                          data-qa-id="logo-uploaded-image"
                          alignItems="flex-start"
                          width={[
                            SEARCH_HIT_IMAGE_WIDTH_SMALL,
                            null,
                            null,
                            null,
                            SEARCH_HIT_IMAGE_WIDTH
                          ]}
                          fsWidth={SEARCH_HIT_IMAGE_WIDTH}
                          handle={logo ? logo.handle : undefined}
                          mimetype={logo ? logo.mimetype : undefined}
                          alt={name}
                          initial={orgName || groupName || name}
                        />
                      )}
                    </LogoContainer>
                  )}
                </LogoBox>

                <InfoBox
                  width="100%"
                  flex="1 1 auto"
                  display="flex"
                  alignItems="center"
                >
                  <Box>
                    {showLabel && (
                      <LabelBox mb={14}>
                        <ListingTypeLabel
                          listingType={type}
                          actionType={actionType}
                        />
                      </LabelBox>
                    )}

                    <Heading lineHeight={1.25}>
                      {disabled ? (
                        <>
                          {name.slice(0, 120)}
                          {name.length > 120 && (
                            <span
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{ __html: '&hellip;' }}
                            />
                          )}
                        </>
                      ) : typeof url === 'object' ? (
                        <>
                          <ListingRouterLink
                            data-qa-id="search-result-link"
                            to={{
                              pathname: url[USER_LOCALE],
                              state: {
                                showSearchContext: true,
                                originatingPathname: window.location.pathname
                              }
                            }}
                            onClick={() => {
                              if (trackClick) trackClick();
                            }}
                          >
                            {name.slice(0, 120)}
                            {name.length > 120 && (
                              <span
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: '&hellip;' }}
                              />
                            )}
                          </ListingRouterLink>
                          <HoverEffect />
                        </>
                      ) : (
                        typeof url === 'string' && ( // Imported url.
                          <>
                            <ListingLink
                              data-qa-id="search-result-link"
                              href={getImportedUrl(url, source)}
                              onClick={() => {
                                if (trackClick) trackClick();
                              }}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {name.slice(0, 120)}
                              {name.length > 120 && (
                                <span
                                  // eslint-disable-next-line react/no-danger
                                  dangerouslySetInnerHTML={{
                                    __html: '&hellip;'
                                  }}
                                />
                              )}
                            </ListingLink>
                            <HoverEffect />
                          </>
                        )
                      )}
                      {isPostedAnonymously && (
                        <Box
                          display="inline-block"
                          ml="4px"
                          verticalAlign="middle"
                        >
                          <Tooltip width={360}>
                            {getText(
                              `The organization has chosen to publish this listing
                              anonymously to maintain confidentiality during the
                              hiring process. They have provided Idealist staff
                              with all of their organization’s details and have
                              been vetted as an organization that meets Idealist’s
                              guidelines for posting on this site.`
                            )}
                          </Tooltip>
                        </Box>
                      )}
                    </Heading>

                    {(orgType === 'CORPORATION' ||
                      (type !== 'ORG' && orgName) ||
                      groupName) && (
                      <Subheading mb={0} lineHeight={1.375}>
                        <Box display="flex" alignItems="center" flexWrap="wrap">
                          {orgType === 'CORPORATION' && (
                            <Tag
                              mr="6px"
                              p="3px 5px"
                              boxShadow={`0 0 0 1px ${colors.silver}`}
                              color={colors.battleshipGrey}
                            >
                              {getText('Business')}
                            </Tag>
                          )}
                          {type !== 'ORG' && orgName && (
                            <>
                              {orgUrl && orgUrl[USER_LOCALE] ? (
                                <StyledRouterLink
                                  to={orgUrl[USER_LOCALE]}
                                  onClick={e => {
                                    e.stopPropagation();
                                    // linkTracker('organization');
                                  }}
                                  data-qa-id="search-result-org-link"
                                >
                                  {orgName}
                                </StyledRouterLink>
                              ) : (
                                orgName
                              )}
                            </>
                          )}
                          {orgType === 'CORPORATION' && (
                            <Box
                              display="inline-block"
                              ml="4px"
                              verticalAlign="middle"
                            >
                              <Tooltip>
                                {/* TRANSLATORS: Tooltip next to Business name for a job posted by business organization type in search results */ getText(
                                  `Social enterprises and approved for-profit companies are allowed to post jobs on Idealist that adhere to our guidelines.`
                                )}
                              </Tooltip>
                            </Box>
                          )}
                          {groupName && (
                            <>
                              {groupUrl ? (
                                <StyledRouterLink
                                  to={groupUrl[USER_LOCALE]}
                                  onClick={e => {
                                    e.stopPropagation();
                                    // linkTracker('group');
                                  }}
                                  data-qa-id="search-result-group-link"
                                >
                                  {groupName}
                                </StyledRouterLink>
                              ) : (
                                groupName
                              )}
                            </>
                          )}
                        </Box>
                      </Subheading>
                    )}

                    {(listingInfo.length > 0 ||
                      listingLocationType !== undefined) && (
                      <Info lineHeight={1.5}>
                        {listingLocationType !== undefined && (
                          <LocationLabel type={listingLocationType} mr="8px" />
                        )}

                        {listingInfo.map((info, index) => (
                          <React.Fragment
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                          >
                            {index > 0 && (
                              <Box
                                display="inline-block"
                                mx="8px"
                                my="-0.2em"
                                bg="currentColor"
                                opacity={0.33}
                                height="1em"
                                width="1px"
                              />
                            )}
                            {info}
                          </React.Fragment>
                        ))}
                      </Info>
                    )}

                    {(startDateTime || endDateTime) && (
                      <DateInfo
                        lineHeight={1.5}
                        color={colors.battleshipGrey}
                        ml="2.3px"
                      >
                        <Icon
                          name="calendar"
                          width={14}
                          fill="currentColor"
                          m="-2.3px"
                        />
                        <Box component="span" ml="6px">
                          {hit.startDate !== hit.endDate && startDateTime ? (
                            <>
                              {shorterDate(startDateTime)}
                              {'  '}
                              {shorterTime(
                                startDateTime,
                                timezone,
                                !endDateTime
                              )}
                              {endDateTime && (
                                <>
                                  {' – '}
                                  {shorterDate(endDateTime)}
                                  {'  '}
                                  {shorterTime(endDateTime, timezone, true)}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {startDateTime
                                ? shorterDate(startDateTime)
                                : endDateTime
                                ? shorterDate(endDateTime)
                                : ''}
                              <Box component="span" ml="8px">
                                {' '}
                              </Box>
                              {startDateTime &&
                                shorterTime(
                                  startDateTime,
                                  timezone,
                                  !endDateTime
                                )}
                              {startDateTime && endDateTime && ' – '}
                              {endDateTime &&
                                shorterTime(endDateTime, timezone, true)}
                            </>
                          )}
                        </Box>
                      </DateInfo>
                    )}

                    {(listingStats.length > 0 || application) && (
                      <Stats color="battleshipGrey" lineHeight={1.5}>
                        {listingStats.map((info, index) => (
                          <React.Fragment
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                          >
                            {index > 0 && (
                              <Box
                                display="inline-block"
                                mx="8px"
                                my="-0.2em"
                                bg="currentColor"
                                opacity={0.33}
                                height="1em"
                                width="1px"
                              />
                            )}
                            {info}
                          </React.Fragment>
                        ))}

                        {application && (
                          <>
                            <ListingStatusLabel
                              listingStatus={application.status}
                              asTag
                              forceSolid
                            />

                            <Box display="inline-block" ml={12}>
                              {sprintf(getText('Applied %(date)s'), {
                                date: shorterDate(application.created)
                              })}
                            </Box>
                          </>
                        )}
                      </Stats>
                    )}

                    {isImported &&
                      typeof amountTotal === 'number' &&
                      typeof amountCurrent === 'number' && (
                        <FundingMeter
                          total={amountTotal}
                          current={amountCurrent}
                          mt="8px"
                          dollars={['DONATION', 'LOAN'].includes(actionType)}
                          signatures={actionType === 'PETITION'}
                        />
                      )}
                  </Box>
                </InfoBox>
              </Box>

              {(showCta || canSave) && (
                <Actions
                  width={[undefined, null, 150]}
                  mb={[0, null, -14]}
                  mt={[12, null, -14]}
                  pl={[0, null, 12]}
                  display="flex"
                  flexDirection={['row', null, 'column']}
                  justifyContent={['flex-start', null, 'center']}
                  ml={[0, SEARCH_HIT_IMAGE_WIDTH_SMALL + 30, 'auto']}
                  flex="0 0 auto"
                >
                  {showCta && (
                    <CtaButton
                      color={colors.waterBlue}
                      hit={hit}
                      isImported={isImported}
                      applications={applications}
                    />
                  )}
                  {canSave && (
                    <>
                      {showCta && (
                        <Hide pointerEvents="auto" xs sm>
                          <Divider opacity="0.2" my={12} />
                        </Hide>
                      )}
                      <SecondaryButton
                        ml={[showCta && 16, null, 0]}
                        saved={saved}
                        onClick={saved ? unsaveListing : saveListing}
                        data-qa-id={saved ? 'unsave-listing' : 'save-listing'}
                      >
                        <Icon name="heart" width={13} height={13} />
                        {saved ? getText('Unsave') : getText('Save')}
                      </SecondaryButton>
                    </>
                  )}
                </Actions>
              )}
            </Box>
          </SmallTile>
        </SearchTileContainer>
      </div>
    );
  }
);

const mapStateToProps = ({ orgs: { byId } }) => ({
  ownedOrgsById: byId
});

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    // $FlowFixMe
    { partialUpdateOrg: partialUpdateOrgRaw }
  )(SearchHit)
);
